import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { UserContext } from "../../../../UserContext";
import actionPlanService from "../../../../services/actionPlanService";
import pot2 from "../../../../assets/pot2.png";
import pot3 from "../../../../assets/pot3.png";
import fullPot from "../../../../assets/full_pot.png";
import flowerBanner from "../../../../assets/flower.png";
import dartIcon from "../../../../assets/dart.png";
import penEdit from "../../../../assets/pen_edit.png";
import saveIcon from "../../../../assets/save.png";
import concentricDull from "../../../../assets/concentric_dull.png";
import concentric from "../../../../assets/concentric_arrow.png";
import arrow from "../../../../assets/arrow.png";
import meter from "../../../../assets/meter.png";
import CheckIn from "./CheckIn";
import EditGoals from "./EditGoals";
import SaveGoals from "./SaveGoals";
import AddNewObjective from "./AddNewObjective";

// ✅ Helper to get pot description - WORKS FOR ANY POT NAME FORMAT
const getPotDescription = (potTitle) => {
  const titleLower = (potTitle || "").toLowerCase();
  
  if (titleLower.includes("visibility")) {
    return "Making your work and impact visible to the right decision-makers.";
  }
  if (titleLower.includes("alignment")) {
    return "Focusing your effort on what leadership actually cares about.";
  }
  if (titleLower.includes("communication") || titleLower.includes("presence")) {
    return "Communicating with clarity and confidence expected at higher levels.";
  }
  if (titleLower.includes("identity") || titleLower.includes("becoming")) {
    return "Showing up as the role you want before you're promoted.";
  }
  if (titleLower.includes("pitch") || titleLower.includes("readiness")) {
    return "Clearly articulating your value and readiness when it matters.";
  }
  
  return "";
};

// ✅ Helper function to determine which pot image to use
const getPotImage = (pot) => {
  if (!pot) return pot3;
  
  const hasActiveObjectives = pot.objectives?.some(obj => obj.status === "active");
  
  // All objectives completed - show full pot
  if (!hasActiveObjectives && pot.objectives && pot.objectives.length > 0) {
    return fullPot;
  }
  
  // XP > 0 - show budding sapling
  if (pot.xp_total > 0) {
    return pot2;
  }
  
  // XP = 0 - show initial pot
  return pot3;
};

/* ---------------------------
   Timestamp + Sorting Helpers
   --------------------------- */

// Robust timestamp normalizer -> always returns milliseconds (number)
const getTimestampMs = (value) => {
  if (!value) return 0;

  if (typeof value === "number") {
    // if it's in seconds (10 digits), convert to ms
    return value < 1e12 ? value * 1000 : value;
  }

  // try Date.parse on string (ISO or other)
  const parsed = Date.parse(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Sort objectives by latest updated_at, fallback created_at, newest-first
const sortObjectivesByLatest = (objectives = []) => {
  return [...(objectives || [])].sort((a, b) => {
    const timeA = getTimestampMs(a.updated_at) || getTimestampMs(a.created_at) || 0;
    const timeB = getTimestampMs(b.updated_at) || getTimestampMs(b.created_at) || 0;
    return timeB - timeA;
  });
};

/* ---------------------------
   ObjectiveContent component
   (unchanged behavior rendering expansion)
   --------------------------- */

const ObjectiveContent = ({ 
  currentObjective, 
  pot, 
  isExpanded,
  onToggleExpand,
  onCheckIn, 
  onEdit, 
  onComplete 
}) => {
  const lineStartRef = useRef(null);
  const lastArrowRef = useRef(null);
  const [lineHeight, setLineHeight] = useState(400);

  const shortTermInitiatives = currentObjective?.initiatives?.filter(
    (init) => init.horizon === "short_term"
  ) || [];
  
  const mediumTermInitiatives = currentObjective?.initiatives?.filter(
    (init) => init.horizon === "medium_term"
  ) || [];

  useEffect(() => {
    if (!isExpanded || !currentObjective || !lineStartRef.current || !lastArrowRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      if (lineStartRef.current && lastArrowRef.current) {
        try {
          const startRect = lineStartRef.current.getBoundingClientRect();
          const arrowRect = lastArrowRef.current.getBoundingClientRect();
          const calculatedHeight = arrowRect.top - startRect.top - 34;
          setLineHeight(calculatedHeight);
        } catch (error) {
          console.error("Error calculating line height:", error);
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [isExpanded, currentObjective, shortTermInitiatives.length, mediumTermInitiatives.length]);

  if (!currentObjective) return null;

  const hasShortTerm = shortTermInitiatives.length > 0;
  const hasMediumTerm = mediumTermInitiatives.length > 0;

  // ✅ COLLAPSED STATE - Show only title with chevron
  if (!isExpanded) {
    return (
      <div className="mt-6 flex items-center cursor-pointer hover:opacity-80" onClick={onToggleExpand}>
        <div className="flex-shrink-0 flex flex-col items-center mr-4">
          <img
            src={concentricDull}
            alt=""
            className="w-[38px] h-[38px] object-cover"
          />
        </div>

        <div className="flex-1 flex items-center justify-between">
          <p className="text-[21px] leading-[1.35] font-medium text-[#939393]">
            {currentObjective.title}
          </p>

          {/* Chevron Down Icon */}
          <svg
            className="w-6 h-6 text-[#939393]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    );
  }

  // ✅ EXPANDED STATE - Show full objective with initiatives
  return (
    <div className="mt-6 flex">
      <div className="flex-shrink-0 flex flex-col items-center mr-4 relative">
        <img
          ref={lineStartRef}
          src={concentric}
          alt=""
          className="w-[38px] h-[38px] object-cover relative z-10"
        />
        
        <div 
          className="absolute left-1/2 top-[38px] -translate-x-1/2 border-l border-[#D1D1D1] z-0"
          style={{ height: `${lineHeight}px` }}
        />
      </div>

      <div className="flex-1">
        <div className="flex items-start">
          <p className="text-[21px] leading-[1.35] font-medium text-[#424242] flex-1">
            {currentObjective.title}
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onCheckIn(pot, currentObjective)}
              className="h-[28px] px-4 rounded-full bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
            >
              <span className="text-white text-[12px] leading-[1] font-medium">
                Check-in
              </span>
            </button>

            <button 
              type="button" 
              className="w-[28px] h-[28px] hover:opacity-70 flex-shrink-0"
              onClick={() => onEdit(pot, currentObjective)}
            >
              <img
                src={penEdit}
                alt="Edit"
                className="w-[28px] h-[28px] object-contain"
              />
            </button>

            <div className="relative group flex-shrink-0">
              <button 
                type="button" 
                className="w-[28px] h-[28px] hover:opacity-70 flex items-center justify-center"
                onClick={() => onComplete(currentObjective)}
              >
                <img
                  src={saveIcon}
                  alt="Complete"
                  className="w-[28px] h-[28px] object-contain"
                />
              </button>
              
              <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-[#424242] text-white text-[11px] font-medium rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none shadow-lg z-[9999]">
                Complete Objective
                <div className="absolute top-full right-2 -mt-[1px]">
                  <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#424242]"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Short Term Initiatives */}
        {hasShortTerm && (
          <div className="mt-4 relative">
            <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
              Short term initiatives
            </p>

            <div className="mt-3 space-y-3">
              {shortTermInitiatives.map((initiative, index) => {
                const isLastArrow = !hasMediumTerm && index === shortTermInitiatives.length - 1;
                
                return (
                  <div key={initiative.id} className="relative">
                    <img
                      ref={isLastArrow ? lastArrowRef : null}
                      src={arrow}
                      alt=""
                      className="absolute -left-[35px] top-[10px] w-[17.6px] h-auto object-contain z-10"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img
                          src={meter}
                          alt=""
                          className="w-[30.5px] h-[30.5px] object-contain"
                        />
                        <p className="flex-1 text-[15px] leading-[1.45] font-normal text-[#000000]">
                          {initiative.title}
                        </p>
                      </div>
                      <p className="text-[12px] leading-[1.1] font-medium uppercase text-[#54B0AF]">
                        +{initiative.xp_value}XP
                      </p>
                    </div>
                    <div className="mt-2 w-[695px] border-b border-[#E4E4E4]" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Medium Term Initiatives */}
        {hasMediumTerm && (
          <div className="mt-12 relative">
            <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
              Medium term initiatives
            </p>

            <div className="mt-3 space-y-3">
              {mediumTermInitiatives.map((initiative, index) => {
                const isLastArrow = index === mediumTermInitiatives.length - 1;
                
                return (
                  <div key={initiative.id} className="relative">
                    <img
                      ref={isLastArrow ? lastArrowRef : null}
                      src={arrow}
                      alt=""
                      className="absolute -left-[35px] top-[10px] w-[17.6px] h-auto object-contain z-10"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img
                          src={meter}
                          alt=""
                          className="w-[30.5px] h-[30.5px] object-contain"
                        />
                        <p className="flex-1 text-[15px] leading-[1.45] font-normal text-[#000000]">
                          {initiative.title}
                        </p>
                      </div>
                      <p className="text-[12px] leading-[1.1] font-medium uppercase text-[#54B0AF]">
                        +{initiative.xp_value}XP
                      </p>
                    </div>
                    <div className="mt-2 w-[695px] border-b border-[#E4E4E4]" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------------------------
   MyActionPlan main component
   --------------------------- */

const MyActionPlan = () => {
  const { user } = useContext(UserContext);
  const userId = user?.user_id;

  // State
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const [pots, setPots] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [expandedPotId, setExpandedPotId] = useState(null);
  const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  
  // Modal states
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isEditGoalOpen, setIsEditGoalOpen] = useState(false);
  const [isSaveGoalOpen, setIsSaveGoalOpen] = useState(false);
  const [isAddObjectiveOpen, setIsAddObjectiveOpen] = useState(false);
  
  // Current selections
  const [currentPot, setCurrentPot] = useState(null);
  const [currentObjective, setCurrentObjective] = useState(null);

  const loadActionPlan = useCallback(async () => {
    try {
      
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      
      const data = await actionPlanService.getActionPlan(userId);
      
      if (!data?.pots?.length) {
        console.warn("No pots found in API response");
        setPots([]);
        setLoading(false);
        return;
      }
  
      // Normalize & sort objectives INSIDE each pot (newest first)
      const normalizedPots = data.pots.map((pot) => ({
        ...pot,
        objectives: sortObjectivesByLatest(pot.objectives),
      }));
  
      setPots(normalizedPots);
  
      // ✅ ALWAYS expand the first pot and its newest active objective
      const firstPot = normalizedPots[0];
      const firstActiveObjective = firstPot?.objectives?.find(
        obj => obj.status === "active"
      );
      
      setExpandedPotId(firstPot?.id || null);
      setExpandedObjectiveId(firstActiveObjective?.id || null);
  
    } catch (error) {
      console.error("❌ Error loading action plan:", error);
      alert("Failed to load action plan. Please refresh the page.");
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
    }
  }, [userId]); // ✅ Removed expandedPotId and expandedObjectiveId from dependencies
  

  const loadTimeline = useCallback(async () => {
    try {
      const data = await actionPlanService.getTimeline(userId, 10);
      setTimeline(data.events || []);
    } catch (error) {
      console.error("Error loading timeline:", error);
      setTimeline([]);
    }
  }, [userId]);

  // Load action plan on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userId) {
      loadActionPlan();
      loadTimeline();
    }
  }, [userId, loadActionPlan, loadTimeline]);

  useEffect(() => {
    if (!expandedObjectiveId) return;
  
    const el = document.getElementById(`objective-${expandedObjectiveId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [expandedObjectiveId]);
  

  const handleExpand = (potId) => {
    if (expandedPotId === potId) {
      // collapse both pot and objective
      setExpandedPotId(null);
      setExpandedObjectiveId(null);
      return;
    }

    const pot = pots.find((p) => p.id === potId);
    const newestActive = pot?.objectives?.find((obj) => obj.status === "active");

    setExpandedPotId(potId);
    setExpandedObjectiveId(newestActive?.id || null);
  };

  const handleToggleObjective = (objectiveId) => {
    // Toggle: if same -> collapse, else open new id
    setExpandedObjectiveId((prev) => (prev === objectiveId ? null : objectiveId));
  };

  const handleCheckInClick = (pot, objective) => {
    setCurrentPot(pot);
    setCurrentObjective(objective);
    setIsCheckInOpen(true);
  };

  const handleCheckInSave = async (checkInData) => {
    try {
      const result = await actionPlanService.submitCheckIn(
        userId,
        currentPot.id,
        checkInData.items,
        checkInData.message
      );
      
      console.log("✅ Check-in saved:", result);
      
      await loadActionPlan();
      await loadTimeline();
      
      setIsCheckInOpen(false);
      setCurrentPot(null);
      setCurrentObjective(null);
    } catch (error) {
      console.error("Error saving check-in:", error);
      alert("Failed to save check-in. Please try again.");
    }
  };

  const handleEditGoalClick = (pot, objective) => {
    setCurrentPot(pot);
    setCurrentObjective(objective);
    setIsEditGoalOpen(true);
  };

  const handleEditGoalSave = async () => {
    try {
      console.log("✅ Goal edits saved, refreshing action plan...");
      
      await loadActionPlan();
      await loadTimeline();
      
      setIsEditGoalOpen(false);
      setCurrentPot(null);
      setCurrentObjective(null);
    } catch (error) {
      console.error("Error refreshing after edit:", error);
    }
  };

  const handleCompleteObjective = async (objective) => {
    setCurrentObjective(objective);
    
    try {
      await actionPlanService.completeObjective(userId, objective.id);
      
      console.log("✅ Objective completed");
      
      await loadActionPlan();
      await loadTimeline();
      
      setIsSaveGoalOpen(true);
    } catch (error) {
      console.error("Error completing objective:", error);
      alert("Failed to complete objective. Please try again.");
    }
  };

  // ✅ NEW: Handle Undo Completion
  const handleUndoComplete = async (objective) => {
    try {
      await actionPlanService.undoCompleteObjective(userId, objective.id);
      
      console.log("✅ Objective completion undone");
      
      await loadActionPlan();
      await loadTimeline();
    } catch (error) {
      console.error("Error undoing objective completion:", error);
      alert("Failed to undo objective completion. Please try again.");
    }
  };

  const handleNextObjective = () => {
    setIsSaveGoalOpen(false);
    
    const pot = pots.find(p => 
      p.objectives.some(obj => obj.id === currentObjective.id)
    );
    
    if (pot) {
      const nextObjective = pot.objectives.find(
        obj => obj.status === "active" && obj.id !== currentObjective.id
      );
      
      if (nextObjective) {
        setCurrentObjective(nextObjective);
        setCurrentPot(pot);
        setExpandedObjectiveId(nextObjective.id);
      } else {
        setCurrentObjective(null);
        setCurrentPot(null);
      }
    }
  };

  const handleAddObjectiveClick = (pot) => {
    setCurrentPot(pot);
    setIsAddObjectiveOpen(true);
  };

  const handleAddObjectiveSave = async (newObjectiveData) => {
    try {
      await actionPlanService.createObjective(
        userId,
        currentPot.id,
        newObjectiveData.title,
        newObjectiveData.initiatives
      );
      
      console.log("✅ New objective created");
      
      await loadActionPlan();
      await loadTimeline();
      
      setIsAddObjectiveOpen(false);
      setCurrentPot(null);
    } catch (error) {
      console.error("Error creating objective:", error);
      alert("Failed to create objective. Please try again.");
    }
  };

  const renderExpandedContent = (pot) => {
    // NOTE: objectives already sorted in state (sortObjectivesByLatest during load)
    const activeObjectives = (pot.objectives || []).filter(obj => obj.status === "active");
    const completedObjectives = (pot.objectives || []).filter(obj => obj.status === "completed");
    const hasCompletedObjectives = completedObjectives.length > 0;
    
    // ✅ Get dynamic pot image
    const potImage = getPotImage(pot);
    const potDescription = getPotDescription(pot.title);

    if (activeObjectives.length === 0 && !hasCompletedObjectives) {
      return (
        <div className="px-6 py-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <img
                src={potImage}
                alt={pot.title}
                className={`object-contain ${
                  potImage === fullPot 
                    ? 'w-[120px] h-[140px]'
                    : 'w-[79px] h-[96px]'
                }`}
              />
              <div className="max-w-[500px]">
                <p className="text-[19px] font-medium text-black">{pot.title}</p>
                {potDescription && (
                  <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
                    {potDescription}
                  </p>
                )}
              </div>
            </div>
            <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
              {pot.xp_total}XP EARNED
            </p>
          </div>

          <div className="mt-6 flex items-center">
            <img
              src={concentricDull}
              alt=""
              className="w-[38.6px] h-[38.6px] object-contain"
            />
            <div className="w-[20.5px] border-b border-[#9C9C9C]" />
            <button
              type="button"
              onClick={() => handleAddObjectiveClick(pot)}
              className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
            >
              <span className="text-white text-[14px] leading-[1] font-medium">
                Add New Objective
              </span>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="px-6 py-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <img
              src={potImage}
              alt={pot.title}
              className={`object-contain ${
                potImage === fullPot 
                  ? 'w-[100px] h-[120px]'
                  : 'w-[79px] h-[96px]'
              }`}
            />
            <div className="max-w-[500px]">
              <p className="text-[19px] font-medium text-black">{pot.title}</p>
              {potDescription && (
                <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
                  {potDescription}
                </p>
              )}
            </div>
          </div>

          <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
            {pot.xp_total}XP EARNED
          </p>
        </div>

        {/* ✅ Render all active objectives with accordion behavior - state is already sorted */}
        {activeObjectives.map((objective) => (
          <div key={objective.id} id={`objective-${objective.id}`}>
            <ObjectiveContent 
              currentObjective={objective}
              pot={pot}
              isExpanded={expandedObjectiveId === objective.id}
              onToggleExpand={() => handleToggleObjective(objective.id)}
              onCheckIn={handleCheckInClick}
              onEdit={handleEditGoalClick}
              onComplete={handleCompleteObjective}
            />
          </div>
        ))}

        {/* ✅ Add New Objective Button Logic */}
        {hasCompletedObjectives ? (
          // Case 2: Has completed objectives - show button above completed section
          <>
            <div className="mt-6 flex items-center">
              <img
                src={concentricDull}
                alt=""
                className="w-[38.6px] h-[38.6px] object-contain"
              />
              <div className="w-[20.5px] border-b border-[#9C9C9C]" />
              <button
                type="button"
                onClick={() => handleAddObjectiveClick(pot)}
                className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
              >
                <span className="text-white text-[14px] leading-[1] font-medium">
                  Add New Objective
                </span>
              </button>
            </div>

            {/* Completed Objectives Section - sorted already */}
            <div className="mt-6">
              <div className="flex items-center">
                <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
                  Completed Objectives
                </p>
                <div className="ml-4 flex-1 border-b border-[#E0E0E0]" />
              </div>

              <div className="mt-4 space-y-4">
                {completedObjectives.map((objective) => (
                  <div key={objective.id} className="flex items-start gap-4">
                    <img
                      src={dartIcon}
                      alt=""
                      className="w-[48px] h-[48px] object-contain flex-shrink-0"
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <p className="text-[21px] leading-[1.35] font-medium text-[#939393] line-through">
                        {objective.title}
                      </p>
                      
                      {/* ✅ Undo Completion Button */}
                      <button
                        type="button"
                        onClick={() => handleUndoComplete(objective)}
                        className="h-[28px] px-4 rounded-full bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors ml-4"
                      >
                        <span className="text-white text-[12px] leading-[1] font-medium whitespace-nowrap">
                          Undo Completion
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          // Case 1: No completed objectives - show button below last incomplete objective
          <div className="mt-6 flex items-center">
            <img
              src={concentricDull}
              alt=""
              className="w-[38.6px] h-[38.6px] object-contain"
            />
            <div className="w-[20.5px] border-b border-[#9C9C9C]" />
            <button
              type="button"
              onClick={() => handleAddObjectiveClick(pot)}
              className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
            >
              <span className="text-white text-[14px] leading-[1] font-medium">
                Add New Objective
              </span>
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full w-full bg-[#F5F6F8] flex items-center justify-center">
        <p className="text-[18px] text-[#424242]">Loading your action plan...</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-[#F5F6F8] overflow-y-auto">
      <div className="max-w-[1200px] mx-auto py-8 px-6">
        <div className="text-left max-w-3xl mb-8">
          <h1 className="text-[34px] leading-[1.35] font-medium text-black mb-3">
            My Action Plan
          </h1>
          <p className="text-[17px] leading-[1.65] text-black/80">
            Nurture your ambitions by breaking big goals into manageable
            <br />
            initiatives. Complete your weekly check-ins to water your pots and
            <br />
            complete your goals to make your plants bloom.
          </p>
        </div>

        <div className="flex gap-8">
          <div className="relative">
            <img
              src={flowerBanner}
              alt=""
              className="absolute right-[-5px] top-[-105px] w-[249px] h-[174px] object-contain z-30"
            />

            <div className="w-[850px] rounded-[20px] border border-[#E3E3E3] bg-white mt-8 overflow-hidden">
              <div className="w-full h-[70px] bg-black flex items-center px-8 rounded-t-[20px]">
                <h2 className="text-[16px] leading-[1.35] font-medium text-white uppercase">
                  Your Progression Goals
                </h2>
              </div>

              <div className="mt-6 px-12 pb-6">
                {pots.length === 0 ? (
                  <p className="text-center text-[#8C8C8C] py-8">
                    No pots found. Please refresh the page.
                  </p>
                ) : (
                  pots.map((pot, index) => {
                    // Get dynamic pot image for collapsed view
                    const potImage = getPotImage(pot);
                    const potDescription = getPotDescription(pot.title);
                    
                    return (
                      <div key={pot.id}>
                        {expandedPotId === pot.id ? (
                          renderExpandedContent(pot)
                        ) : (
                          <div className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-6">
                              <img
                                src={potImage}
                                alt={pot.title}
                                className={`object-contain ${
                                  potImage === fullPot 
                                    ? 'w-[70px] h-[90px]'
                                    : 'w-[51.9px] h-[64.86px]'
                                }`}
                              />
                              <div className="max-w-[400px]">
                                <p className="text-[19px] leading-[1.35] font-medium text-black">
                                  {pot.title}
                                </p>
                                {potDescription && (
                                  <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
                                    {potDescription}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="text-right">
                              <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
                                {pot.xp_total}XP EARNED
                              </p>
                              <button
                                type="button"
                                onClick={() => handleExpand(pot.id)}
                                className="mt-1 text-[11px] leading-[1] font-medium uppercase text-[#4C9E9E] hover:text-[#259594]"
                              >
                                Expand
                              </button>
                            </div>
                          </div>
                        )}

                        {index !== pots.length - 1 && expandedPotId !== pot.id && (
                          <div className="px-4">
                            <div className="w-[765px] max-w-full border-b border-[#E0E0E0]" />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="w-[320px] pt-10">
            <h3 className="text-[14px] leading-[20px] font-medium uppercase text-[#1A1F36]">
              Your Checkin Timeline
            </h3>
            <div className="mt-2 w-[305px] border-b border-[#D3D3D3]" />

            <div className="mt-4 relative">
              {timeline.length > 0 && (
                <div className="absolute left-[11px] top-[25px] h-[calc(100%-25px)] border-l border-[#CFCFCF]" />
              )}

              <div className="space-y-6">
                {timeline.length === 0 ? (
                  <p className="text-[14px] text-[#A5ACB8] mt-4">
                    No timeline events yet. Start checking in!
                  </p>
                ) : (
                  timeline.slice(0, 10).map((event, idx) => (
                    <div key={`${event.created_at}-${idx}`} className="flex items-start">
                      <div className="flex-shrink-0 relative z-10 mt-[2px]">
                        <img
                          src={dartIcon}
                          alt=""
                          className="w-[31.8px] h-[31.8px] object-contain"
                        />
                      </div>

                      <div className="ml-4">
                        <p className="text-[12px] leading-[20px] text-[#A5ACB8]">
                          {new Date(event.created_at).toLocaleString()}
                        </p>
                        <p className="text-[14px] leading-[20px] text-[#1A1F36] mt-1">
                          {event.message}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <CheckIn
        isOpen={isCheckInOpen}
        onClose={() => setIsCheckInOpen(false)}
        onSave={handleCheckInSave}
        pot={currentPot}
        objective={currentObjective}
      />

      <EditGoals
        isOpen={isEditGoalOpen}
        onClose={() => setIsEditGoalOpen(false)}
        onSave={handleEditGoalSave}
        pot={currentPot}
        objective={currentObjective}
        userId={userId}
      />

      <SaveGoals
        isOpen={isSaveGoalOpen}
        onClose={() => setIsSaveGoalOpen(false)}
        onNextObjective={handleNextObjective}
        objectiveText={currentObjective?.title || ""}
      />

      <AddNewObjective
        isOpen={isAddObjectiveOpen}
        onClose={() => setIsAddObjectiveOpen(false)}
        onSave={handleAddObjectiveSave}
        goalTitle={currentPot?.title?.replace?.(' Pot', '') || 'Visibility'}
      />
    </div>
  );
};

export default MyActionPlan;


// // stocai_frontend/src/components/HomePage/components/Action/MyActionPlan.jsx

// import React, { useEffect, useState, useContext, useRef } from "react";
// import { UserContext } from "../../../../UserContext";
// import actionPlanService from "../../../../services/actionPlanService";
// import pot2 from "../../../../assets/pot2.png";
// import pot3 from "../../../../assets/pot3.png";
// import fullPot from "../../../../assets/full_pot.png";
// import flowerBanner from "../../../../assets/flower.png";
// import dartIcon from "../../../../assets/dart.png";
// import penEdit from "../../../../assets/pen_edit.png";
// import saveIcon from "../../../../assets/save.png";
// import concentricDull from "../../../../assets/concentric_dull.png";
// import concentric from "../../../../assets/concentric_arrow.png";
// import arrow from "../../../../assets/arrow.png";
// import meter from "../../../../assets/meter.png";
// import CheckIn from "./CheckIn";
// import EditGoals from "./EditGoals";
// import SaveGoals from "./SaveGoals";
// import AddNewObjective from "./AddNewObjective";

// // ✅ Helper to get pot description - WORKS FOR ANY POT NAME FORMAT
// const getPotDescription = (potTitle) => {
//   const titleLower = potTitle.toLowerCase();
  
//   if (titleLower.includes("visibility")) {
//     return "Making your work and impact visible to the right decision-makers.";
//   }
//   if (titleLower.includes("alignment")) {
//     return "Focusing your effort on what leadership actually cares about.";
//   }
//   if (titleLower.includes("communication") || titleLower.includes("presence")) {
//     return "Communicating with clarity and confidence expected at higher levels.";
//   }
//   if (titleLower.includes("identity") || titleLower.includes("becoming")) {
//     return "Showing up as the role you want before you're promoted.";
//   }
//   if (titleLower.includes("pitch") || titleLower.includes("readiness")) {
//     return "Clearly articulating your value and readiness when it matters.";
//   }
  
//   return "";
// };

// // ✅ Helper function to determine which pot image to use
// const getPotImage = (pot) => {
//   if (!pot) return pot3;
  
//   const hasActiveObjectives = pot.objectives.some(obj => obj.status === "active");
  
//   // All objectives completed - show full pot
//   if (!hasActiveObjectives && pot.objectives.length > 0) {
//     return fullPot;
//   }
  
//   // XP > 0 - show budding sapling
//   if (pot.xp_total > 0) {
//     return pot2;
//   }
  
//   // XP = 0 - show initial pot
//   return pot3;
// };

// // ✅ Component for rendering objective with dynamic line and collapse/expand
// const ObjectiveContent = ({ 
//   currentObjective, 
//   pot, 
//   isExpanded,
//   onToggleExpand,
//   onCheckIn, 
//   onEdit, 
//   onComplete 
// }) => {
//   const lineStartRef = useRef(null);
//   const lastArrowRef = useRef(null);
//   const [lineHeight, setLineHeight] = useState(400);

//   const shortTermInitiatives = currentObjective?.initiatives?.filter(
//     (init) => init.horizon === "short_term"
//   ) || [];
  
//   const mediumTermInitiatives = currentObjective?.initiatives?.filter(
//     (init) => init.horizon === "medium_term"
//   ) || [];

//   useEffect(() => {
//     if (!isExpanded || !currentObjective || !lineStartRef.current || !lastArrowRef.current) {
//       return;
//     }

//     const timer = setTimeout(() => {
//       if (lineStartRef.current && lastArrowRef.current) {
//         try {
//           const startRect = lineStartRef.current.getBoundingClientRect();
//           const arrowRect = lastArrowRef.current.getBoundingClientRect();
//           const calculatedHeight = arrowRect.top - startRect.top - 34;
//           setLineHeight(calculatedHeight);
//         } catch (error) {
//           console.error("Error calculating line height:", error);
//         }
//       }
//     }, 50);

//     return () => clearTimeout(timer);
//   }, [isExpanded, currentObjective, shortTermInitiatives.length, mediumTermInitiatives.length]);

//   if (!currentObjective) return null;

//   const hasShortTerm = shortTermInitiatives.length > 0;
//   const hasMediumTerm = mediumTermInitiatives.length > 0;

//   // ✅ COLLAPSED STATE - Show only title with chevron
//   if (!isExpanded) {
//     return (
//       <div className="mt-6 flex items-center cursor-pointer hover:opacity-80" onClick={onToggleExpand}>
//         <div className="flex-shrink-0 flex flex-col items-center mr-4">
//           <img
//             src={concentricDull}
//             alt=""
//             className="w-[38px] h-[38px] object-cover"
//           />
//         </div>

//         <div className="flex-1 flex items-center justify-between">
//           <p className="text-[21px] leading-[1.35] font-medium text-[#939393]">
//             {currentObjective.title}
//           </p>

//           {/* Chevron Down Icon */}
//           <svg
//             className="w-6 h-6 text-[#939393]"
//             fill="none"
//             stroke="currentColor"
//             viewBox="0 0 24 24"
//           >
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
//           </svg>
//         </div>
//       </div>
//     );
//   }

//   // ✅ EXPANDED STATE - Show full objective with initiatives
//   return (
//     <div className="mt-6 flex">
//       <div className="flex-shrink-0 flex flex-col items-center mr-4 relative">
//         <img
//           ref={lineStartRef}
//           src={concentric}
//           alt=""
//           className="w-[38px] h-[38px] object-cover relative z-10"
//         />
        
//         <div 
//           className="absolute left-1/2 top-[38px] -translate-x-1/2 border-l border-[#D1D1D1] z-0"
//           style={{ height: `${lineHeight}px` }}
//         />
//       </div>

//       <div className="flex-1">
//         <div className="flex items-start">
//           <p className="text-[21px] leading-[1.35] font-medium text-[#424242] flex-1">
//             {currentObjective.title}
//           </p>

//           <div className="flex items-center gap-2">
//             <button
//               type="button"
//               onClick={() => onCheckIn(pot, currentObjective)}
//               className="h-[28px] px-4 rounded-full bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
//             >
//               <span className="text-white text-[12px] leading-[1] font-medium">
//                 Check-in
//               </span>
//             </button>

//             <button 
//               type="button" 
//               className="w-[28px] h-[28px] hover:opacity-70 flex-shrink-0"
//               onClick={() => onEdit(pot, currentObjective)}
//             >
//               <img
//                 src={penEdit}
//                 alt="Edit"
//                 className="w-[28px] h-[28px] object-contain"
//               />
//             </button>

//             <div className="relative group flex-shrink-0">
//               <button 
//                 type="button" 
//                 className="w-[28px] h-[28px] hover:opacity-70 flex items-center justify-center"
//                 onClick={() => onComplete(currentObjective)}
//               >
//                 <img
//                   src={saveIcon}
//                   alt="Complete"
//                   className="w-[28px] h-[28px] object-contain"
//                 />
//               </button>
              
//               <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-[#424242] text-white text-[11px] font-medium rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none shadow-lg z-[9999]">
//                 Complete Objective
//                 <div className="absolute top-full right-2 -mt-[1px]">
//                   <div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#424242]"></div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Short Term Initiatives */}
//         {hasShortTerm && (
//           <div className="mt-4 relative">
//             <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
//               Short term initiatives
//             </p>

//             <div className="mt-3 space-y-3">
//               {shortTermInitiatives.map((initiative, index) => {
//                 const isLastArrow = !hasMediumTerm && index === shortTermInitiatives.length - 1;
                
//                 return (
//                   <div key={initiative.id} className="relative">
//                     <img
//                       ref={isLastArrow ? lastArrowRef : null}
//                       src={arrow}
//                       alt=""
//                       className="absolute -left-[35px] top-[10px] w-[17.6px] h-auto object-contain z-10"
//                     />
                    
//                     <div className="flex items-center justify-between">
//                       <div className="flex items-center gap-4">
//                         <img
//                           src={meter}
//                           alt=""
//                           className="w-[30.5px] h-[30.5px] object-contain"
//                         />
//                         <p className="flex-1 text-[15px] leading-[1.45] font-normal text-[#000000]">
//                           {initiative.title}
//                         </p>
//                       </div>
//                       <p className="text-[12px] leading-[1.1] font-medium uppercase text-[#54B0AF]">
//                         +{initiative.xp_value}XP
//                       </p>
//                     </div>
//                     <div className="mt-2 w-[695px] border-b border-[#E4E4E4]" />
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         )}

//         {/* Medium Term Initiatives */}
//         {hasMediumTerm && (
//           <div className="mt-12 relative">
//             <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
//               Medium term initiatives
//             </p>

//             <div className="mt-3 space-y-3">
//               {mediumTermInitiatives.map((initiative, index) => {
//                 const isLastArrow = index === mediumTermInitiatives.length - 1;
                
//                 return (
//                   <div key={initiative.id} className="relative">
//                     <img
//                       ref={isLastArrow ? lastArrowRef : null}
//                       src={arrow}
//                       alt=""
//                       className="absolute -left-[35px] top-[10px] w-[17.6px] h-auto object-contain z-10"
//                     />
                    
//                     <div className="flex items-center justify-between">
//                       <div className="flex items-center gap-4">
//                         <img
//                           src={meter}
//                           alt=""
//                           className="w-[30.5px] h-[30.5px] object-contain"
//                         />
//                         <p className="flex-1 text-[15px] leading-[1.45] font-normal text-[#000000]">
//                           {initiative.title}
//                         </p>
//                       </div>
//                       <p className="text-[12px] leading-[1.1] font-medium uppercase text-[#54B0AF]">
//                         +{initiative.xp_value}XP
//                       </p>
//                     </div>
//                     <div className="mt-2 w-[695px] border-b border-[#E4E4E4]" />
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// const MyActionPlan = () => {
//   const { user } = useContext(UserContext);
//   const userId = user?.user_id;

//   // State
//   const [loading, setLoading] = useState(true);
//   const [pots, setPots] = useState([]);
//   const [timeline, setTimeline] = useState([]);
//   const [expandedPotId, setExpandedPotId] = useState(null);
//   const [expandedObjectiveId, setExpandedObjectiveId] = useState(null);
  
//   // Modal states
//   const [isCheckInOpen, setIsCheckInOpen] = useState(false);
//   const [isEditGoalOpen, setIsEditGoalOpen] = useState(false);
//   const [isSaveGoalOpen, setIsSaveGoalOpen] = useState(false);
//   const [isAddObjectiveOpen, setIsAddObjectiveOpen] = useState(false);
  
//   // Current selections
//   const [currentPot, setCurrentPot] = useState(null);
//   const [currentObjective, setCurrentObjective] = useState(null);

//   // Load action plan on mount
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   useEffect(() => {
//     if (userId) {
//       loadActionPlan();
//       loadTimeline();
//     }
//   }, [userId]);

//   const loadActionPlan = async () => {
//     try {
//       console.log("🚀 Starting to load action plan...");
      
//       setLoading(true);
      
//       const data = await actionPlanService.getActionPlan(userId);
      
//       console.log("📦 Raw API response:", data);
      
//       if (!data || !data.pots || data.pots.length === 0) {
//         console.log("⚠️ No pots found after GET, something went wrong");
//         throw new Error("Failed to initialize action plan");
//       }
      
//       console.log("📦 Setting pots:", data.pots);
      
//       setPots(data.pots);
      
//       // ✅ Set first incomplete objective as expanded by default
//       if (data.pots.length > 0) {
//         const firstPot = data.pots[0];
//         const firstIncompleteObjective = firstPot.objectives.find(obj => obj.status === "active");
//         if (firstIncompleteObjective) {
//           setExpandedObjectiveId(firstIncompleteObjective.id);
//         }
//       }
      
//       console.log("✅ Action plan loaded successfully");
//     } catch (error) {
//       console.error("❌ Error loading action plan:", error);
//       alert("Failed to load action plan. Please refresh the page.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const loadTimeline = async () => {
//     try {
//       const data = await actionPlanService.getTimeline(userId, 10);
//       setTimeline(data.events || []);
//     } catch (error) {
//       console.error("Error loading timeline:", error);
//       setTimeline([]);
//     }
//   };

//   const handleExpand = (potId) => {
//     setExpandedPotId(expandedPotId === potId ? null : potId);
//   };

//   const handleToggleObjective = (objectiveId) => {
//     // ✅ Accordion behavior - only one objective expanded at a time
//     setExpandedObjectiveId(expandedObjectiveId === objectiveId ? objectiveId : objectiveId);
//   };

//   const handleCheckInClick = (pot, objective) => {
//     setCurrentPot(pot);
//     setCurrentObjective(objective);
//     setIsCheckInOpen(true);
//   };

//   const handleCheckInSave = async (checkInData) => {
//     try {
//       const result = await actionPlanService.submitCheckIn(
//         userId,
//         currentPot.id,
//         checkInData.items,
//         checkInData.message
//       );
      
//       console.log("✅ Check-in saved:", result);
      
//       await loadActionPlan();
//       await loadTimeline();
      
//       setIsCheckInOpen(false);
//       setCurrentPot(null);
//       setCurrentObjective(null);
//     } catch (error) {
//       console.error("Error saving check-in:", error);
//       alert("Failed to save check-in. Please try again.");
//     }
//   };

//   const handleEditGoalClick = (pot, objective) => {
//     setCurrentPot(pot);
//     setCurrentObjective(objective);
//     setIsEditGoalOpen(true);
//   };

//   const handleEditGoalSave = async () => {
//     try {
//       console.log("✅ Goal edits saved, refreshing action plan...");
      
//       await loadActionPlan();
//       await loadTimeline();
      
//       setIsEditGoalOpen(false);
//       setCurrentPot(null);
//       setCurrentObjective(null);
//     } catch (error) {
//       console.error("Error refreshing after edit:", error);
//     }
//   };

//   const handleCompleteObjective = async (objective) => {
//     setCurrentObjective(objective);
    
//     try {
//       await actionPlanService.completeObjective(userId, objective.id);
      
//       console.log("✅ Objective completed");
      
//       await loadActionPlan();
//       await loadTimeline();
      
//       setIsSaveGoalOpen(true);
//     } catch (error) {
//       console.error("Error completing objective:", error);
//       alert("Failed to complete objective. Please try again.");
//     }
//   };

//   // ✅ NEW: Handle Undo Completion
//   const handleUndoComplete = async (objective) => {
//     try {
//       await actionPlanService.undoCompleteObjective(userId, objective.id);
      
//       console.log("✅ Objective completion undone");
      
//       await loadActionPlan();
//       await loadTimeline();
//     } catch (error) {
//       console.error("Error undoing objective completion:", error);
//       alert("Failed to undo objective completion. Please try again.");
//     }
//   };

//   const handleNextObjective = () => {
//     setIsSaveGoalOpen(false);
    
//     const pot = pots.find(p => 
//       p.objectives.some(obj => obj.id === currentObjective.id)
//     );
    
//     if (pot) {
//       const nextObjective = pot.objectives.find(
//         obj => obj.status === "active" && obj.id !== currentObjective.id
//       );
      
//       if (nextObjective) {
//         setCurrentObjective(nextObjective);
//         setCurrentPot(pot);
//         setExpandedObjectiveId(nextObjective.id);
//       } else {
//         setCurrentObjective(null);
//         setCurrentPot(null);
//       }
//     }
//   };

//   const handleAddObjectiveClick = (pot) => {
//     setCurrentPot(pot);
//     setIsAddObjectiveOpen(true);
//   };

//   const handleAddObjectiveSave = async (newObjectiveData) => {
//     try {
//       await actionPlanService.createObjective(
//         userId,
//         currentPot.id,
//         newObjectiveData.title,
//         newObjectiveData.initiatives
//       );
      
//       console.log("✅ New objective created");
      
//       await loadActionPlan();
//       await loadTimeline();
      
//       setIsAddObjectiveOpen(false);
//       setCurrentPot(null);
//     } catch (error) {
//       console.error("Error creating objective:", error);
//       alert("Failed to create objective. Please try again.");
//     }
//   };

//   const renderExpandedContent = (pot) => {
//     const activeObjectives = pot.objectives.filter(obj => obj.status === "active");
//     const completedObjectives = pot.objectives.filter(obj => obj.status === "completed");
//     const hasCompletedObjectives = completedObjectives.length > 0;
    
//     // ✅ Get dynamic pot image
//     const potImage = getPotImage(pot);
//     const potDescription = getPotDescription(pot.title);

//     if (activeObjectives.length === 0 && !hasCompletedObjectives) {
//       return (
//         <div className="px-6 py-6">
//           <div className="flex justify-between items-start">
//             <div className="flex items-center gap-4">
//               <img
//                 src={potImage}
//                 alt={pot.title}
//                 className={`object-contain ${
//                   potImage === fullPot 
//                     ? 'w-[120px] h-[140px]'
//                     : 'w-[79px] h-[96px]'
//                 }`}
//               />
//               <div className="max-w-[500px]">
//                 <p className="text-[19px] font-medium text-black">{pot.title}</p>
//                 {potDescription && (
//                   <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
//                     {potDescription}
//                   </p>
//                 )}
//               </div>
//             </div>
//             <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
//               {pot.xp_total}XP EARNED
//             </p>
//           </div>

//           <div className="mt-6 flex items-center">
//             <img
//               src={concentricDull}
//               alt=""
//               className="w-[38.6px] h-[38.6px] object-contain"
//             />
//             <div className="w-[20.5px] border-b border-[#9C9C9C]" />
//             <button
//               type="button"
//               onClick={() => handleAddObjectiveClick(pot)}
//               className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
//             >
//               <span className="text-white text-[14px] leading-[1] font-medium">
//                 Add New Objective
//               </span>
//             </button>
//           </div>
//         </div>
//       );
//     }

//     return (
//       <div className="px-6 py-6">
//         <div className="flex justify-between items-start">
//           <div className="flex items-center gap-4">
//             <img
//               src={potImage}
//               alt={pot.title}
//               className={`object-contain ${
//                 potImage === fullPot 
//                   ? 'w-[100px] h-[120px]'
//                   : 'w-[79px] h-[96px]'
//               }`}
//             />
//             <div className="max-w-[500px]">
//               <p className="text-[19px] font-medium text-black">{pot.title}</p>
//               {potDescription && (
//                 <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
//                   {potDescription}
//                 </p>
//               )}
//             </div>
//           </div>

//           <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
//             {pot.xp_total}XP EARNED
//           </p>
//         </div>

//         {/* ✅ Render all active objectives with accordion behavior */}
//         {activeObjectives.map((objective) => (
//           <ObjectiveContent 
//             key={objective.id}
//             currentObjective={objective}
//             pot={pot}
//             isExpanded={expandedObjectiveId === objective.id}
//             onToggleExpand={() => handleToggleObjective(objective.id)}
//             onCheckIn={handleCheckInClick}
//             onEdit={handleEditGoalClick}
//             onComplete={handleCompleteObjective}
//           />
//         ))}

//         {/* ✅ Add New Objective Button Logic */}
//         {hasCompletedObjectives ? (
//           // Case 2: Has completed objectives - show button above completed section
//           <>
//             <div className="mt-6 flex items-center">
//               <img
//                 src={concentricDull}
//                 alt=""
//                 className="w-[38.6px] h-[38.6px] object-contain"
//               />
//               <div className="w-[20.5px] border-b border-[#9C9C9C]" />
//               <button
//                 type="button"
//                 onClick={() => handleAddObjectiveClick(pot)}
//                 className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
//               >
//                 <span className="text-white text-[14px] leading-[1] font-medium">
//                   Add New Objective
//                 </span>
//               </button>
//             </div>

//             {/* Completed Objectives Section */}
//             <div className="mt-6">
//               <div className="flex items-center">
//                 <p className="text-[11px] font-bold uppercase leading-[1.45] text-[#54B0AF]">
//                   Completed Objectives
//                 </p>
//                 <div className="ml-4 flex-1 border-b border-[#E0E0E0]" />
//               </div>

//               <div className="mt-4 space-y-4">
//                 {completedObjectives.map((objective) => (
//                   <div key={objective.id} className="flex items-start gap-4">
//                     <img
//                       src={dartIcon}
//                       alt=""
//                       className="w-[48px] h-[48px] object-contain flex-shrink-0"
//                     />
//                     <div className="flex-1 flex items-center justify-between">
//                       <p className="text-[21px] leading-[1.35] font-medium text-[#939393] line-through">
//                         {objective.title}
//                       </p>
                      
//                       {/* ✅ Undo Completion Button */}
//                       <button
//                         type="button"
//                         onClick={() => handleUndoComplete(objective)}
//                         className="h-[28px] px-4 rounded-full bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors ml-4"
//                       >
//                         <span className="text-white text-[12px] leading-[1] font-medium whitespace-nowrap">
//                           Undo Completion
//                         </span>
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </>
//         ) : (
//           // Case 1: No completed objectives - show button below last incomplete objective
//           <div className="mt-6 flex items-center">
//             <img
//               src={concentricDull}
//               alt=""
//               className="w-[38.6px] h-[38.6px] object-contain"
//             />
//             <div className="w-[20.5px] border-b border-[#9C9C9C]" />
//             <button
//               type="button"
//               onClick={() => handleAddObjectiveClick(pot)}
//               className="h-[33px] px-7 rounded-[74px] bg-[#0D1111] flex items-center justify-center hover:bg-gray-800 transition-colors"
//             >
//               <span className="text-white text-[14px] leading-[1] font-medium">
//                 Add New Objective
//               </span>
//             </button>
//           </div>
//         )}
//       </div>
//     );
//   };

//   if (loading) {
//     return (
//       <div className="h-full w-full bg-[#F5F6F8] flex items-center justify-center">
//         <p className="text-[18px] text-[#424242]">Loading your action plan...</p>
//       </div>
//     );
//   }

//   return (
//     <div className="h-full w-full bg-[#F5F6F8] overflow-y-auto">
//       <div className="max-w-[1200px] mx-auto py-8 px-6">
//         <div className="text-left max-w-3xl mb-8">
//           <h1 className="text-[34px] leading-[1.35] font-medium text-black mb-3">
//             My Action Plan
//           </h1>
//           <p className="text-[17px] leading-[1.65] text-black/80">
//             Nurture your ambitions by breaking big goals into manageable
//             <br />
//             initiatives. Complete your weekly check-ins to water your pots and
//             <br />
//             complete your goals to make your plants bloom.
//           </p>
//         </div>

//         <div className="flex gap-8">
//           <div className="relative">
//             <img
//               src={flowerBanner}
//               alt=""
//               className="absolute right-[-5px] top-[-105px] w-[249px] h-[174px] object-contain z-30"
//             />

//             <div className="w-[850px] rounded-[20px] border border-[#E3E3E3] bg-white mt-8 overflow-hidden">
//               <div className="w-full h-[70px] bg-black flex items-center px-8 rounded-t-[20px]">
//                 <h2 className="text-[16px] leading-[1.35] font-medium text-white uppercase">
//                   Your Progression Goals
//                 </h2>
//               </div>

//               <div className="mt-6 px-12 pb-6">
//                 {pots.length === 0 ? (
//                   <p className="text-center text-[#8C8C8C] py-8">
//                     No pots found. Please refresh the page.
//                   </p>
//                 ) : (
//                   pots.map((pot, index) => {
//                     // ✅ Get dynamic pot image for collapsed view
//                     const potImage = getPotImage(pot);
//                     const potDescription = getPotDescription(pot.title);
                    
//                     return (
//                       <div key={pot.id}>
//                         {expandedPotId === pot.id ? (
//                           renderExpandedContent(pot)
//                         ) : (
//                           <div className="flex items-center justify-between py-2">
//                             <div className="flex items-center gap-6">
//                               <img
//                                 src={potImage}
//                                 alt={pot.title}
//                                 className={`object-contain ${
//                                   potImage === fullPot 
//                                     ? 'w-[70px] h-[90px]'
//                                     : 'w-[51.9px] h-[64.86px]'
//                                 }`}
//                               />
//                               <div className="max-w-[400px]">
//                                 <p className="text-[19px] leading-[1.35] font-medium text-black">
//                                   {pot.title}
//                                 </p>
//                                 {potDescription && (
//                                   <p className="text-[13px] leading-[1.45] text-[#8C8C8C] mt-1">
//                                     {potDescription}
//                                   </p>
//                                 )}
//                               </div>
//                             </div>

//                             <div className="text-right">
//                               <p className="text-[14px] leading-[1.1] font-medium text-[#8C8C8C] uppercase">
//                                 {pot.xp_total}XP EARNED
//                               </p>
//                               <button
//                                 type="button"
//                                 onClick={() => handleExpand(pot.id)}
//                                 className="mt-1 text-[11px] leading-[1] font-medium uppercase text-[#4C9E9E] hover:text-[#259594]"
//                               >
//                                 Expand
//                               </button>
//                             </div>
//                           </div>
//                         )}

//                         {index !== pots.length - 1 && expandedPotId !== pot.id && (
//                           <div className="px-4">
//                             <div className="w-[765px] max-w-full border-b border-[#E0E0E0]" />
//                           </div>
//                         )}
//                       </div>
//                     );
//                   })
//                 )}
//               </div>
//             </div>
//           </div>

//           <div className="w-[320px] pt-10">
//             <h3 className="text-[14px] leading-[20px] font-medium uppercase text-[#1A1F36]">
//               Your Checkin Timeline
//             </h3>
//             <div className="mt-2 w-[305px] border-b border-[#D3D3D3]" />

//             <div className="mt-4 relative">
//               {timeline.length > 0 && (
//                 <div className="absolute left-[11px] top-[25px] h-[calc(100%-25px)] border-l border-[#CFCFCF]" />
//               )}

//               <div className="space-y-6">
//                 {timeline.length === 0 ? (
//                   <p className="text-[14px] text-[#A5ACB8] mt-4">
//                     No timeline events yet. Start checking in!
//                   </p>
//                 ) : (
//                   timeline.slice(0, 10).map((event, idx) => (
//                     <div key={`${event.created_at}-${idx}`} className="flex items-start">
//                       <div className="flex-shrink-0 relative z-10 mt-[2px]">
//                         <img
//                           src={dartIcon}
//                           alt=""
//                           className="w-[31.8px] h-[31.8px] object-contain"
//                         />
//                       </div>

//                       <div className="ml-4">
//                         <p className="text-[12px] leading-[20px] text-[#A5ACB8]">
//                           {new Date(event.created_at).toLocaleString()}
//                         </p>
//                         <p className="text-[14px] leading-[20px] text-[#1A1F36] mt-1">
//                           {event.message}
//                         </p>
//                       </div>
//                     </div>
//                   ))
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <CheckIn
//         isOpen={isCheckInOpen}
//         onClose={() => setIsCheckInOpen(false)}
//         onSave={handleCheckInSave}
//         pot={currentPot}
//         objective={currentObjective}
//       />

//       <EditGoals
//         isOpen={isEditGoalOpen}
//         onClose={() => setIsEditGoalOpen(false)}
//         onSave={handleEditGoalSave}
//         pot={currentPot}
//         objective={currentObjective}
//         userId={userId}
//       />

//       <SaveGoals
//         isOpen={isSaveGoalOpen}
//         onClose={() => setIsSaveGoalOpen(false)}
//         onNextObjective={handleNextObjective}
//         objectiveText={currentObjective?.title || ""}
//       />

//       <AddNewObjective
//         isOpen={isAddObjectiveOpen}
//         onClose={() => setIsAddObjectiveOpen(false)}
//         onSave={handleAddObjectiveSave}
//         goalTitle={currentPot?.title.replace(' Pot', '') || 'Visibility'}
//       />
//     </div>
//   );
// };

// export default MyActionPlan;